import { isAxiosError } from 'axios';
import { tAxiosCustomConfig } from './axios.types';

/*
  BUG-868 / BUG-1116: eligible hold-arm loads first wait for Firebase under one
  per-document boot deadline. Control loads, exhausted deadlines and SDK/network
  failures can still release degraded requests; this helper retries their 401s
  for SubscriptionProvider and IntegrationsProvider. Every retry re-enters the
  interceptor and asks the live Firebase session for a token again.

  Only 401s stamped authDegraded are retried. A rejected live bearer is not
  retried. Settled-without-user and other refresh rejections stay non-degraded:
  their 401 clears this tab and redirects, skipping Firebase sign-out when the
  request carried no token.
*/

// Three attempts, 1.3 s of backoff, excluding each request's auth wait. With an
// exhausted boot deadline, later attempts use the legacy 3 s readiness wait.
export const AUTH_RETRY_DELAYS_MS: readonly number[] = [400, 900];

// A 401 whose bearer left the interceptor degraded (Firebase had not settled
// when the request was signed). The only failure a plain retry can fix.
export function isDegradedAuthError(error: unknown): boolean {
  return (
    isAxiosError(error) &&
    error.response?.status === 401 &&
    (error.config as tAxiosCustomConfig | undefined)?.authDegraded === true
  );
}

export type tWithAuthRetryOptions = {
  delaysMs?: readonly number[];
  // Caller-owned guards (workspace switch, superseded sequence, unmount).
  // Checked around every wait; once true the last error is re-thrown untouched.
  shouldAbort?: () => boolean;
  onRetry?: (options: { attempt: number; error: unknown }) => void;
};

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function withAuthRetry<T>(
  request: () => Promise<T>,
  options?: tWithAuthRetryOptions,
): Promise<T> {
  const delaysMs = options?.delaysMs ?? AUTH_RETRY_DELAYS_MS;

  for (let attempt = 0; ; attempt += 1) {
    try {
      return await request();
    } catch (error) {
      const outOfDelays = attempt >= delaysMs.length;
      if (!isDegradedAuthError(error) || outOfDelays || options?.shouldAbort?.()) {
        throw error;
      }

      await wait(delaysMs[attempt]);

      if (options?.shouldAbort?.()) {
        throw error;
      }

      options?.onRetry?.({ attempt: attempt + 1, error });
    }
  }
}
